import { useQuery } from "@tanstack/react-query";
import type {
  UiCombinedNomenclature,
  UiCountry,
  UiDeliveryTerms,
  UiEnumClassifier,
  UiModeOfTransport,
  UiNatureOfTransaction,
  UiRegion,
  UiUnitOfMeasure,
} from "@/generated/ui-api";
import * as sdk from "@/generated/ui-api";
import { queryClient } from "@/lib/queryClient";
import i18n from "@/i18n";

export const classifierKeys = {
  all: () => ["classifiers", i18n.language] as const,
  lists: () => [...classifierKeys.all(), "list"] as const,
  regions: (validationDate?: string) =>
    [...classifierKeys.lists(), "regions", { validationDate }] as const,
  natureOfTransactions: (validationDate?: string) =>
    [
      ...classifierKeys.lists(),
      "natureOfTransactions",
      { validationDate },
    ] as const,
  deliveryTerms: (validationDate?: string) =>
    [...classifierKeys.lists(), "deliveryTerms", { validationDate }] as const,
  modeOfTransports: (validationDate?: string) =>
    [
      ...classifierKeys.lists(),
      "modeOfTransports",
      { validationDate },
    ] as const,
  unitOfMeasures: (validationDate?: string) =>
    [...classifierKeys.lists(), "unitOfMeasures", { validationDate }] as const,
  countries: (validationDate?: string) =>
    [...classifierKeys.lists(), "countries", { validationDate }] as const,
  tradeDirections: () =>
    [...classifierKeys.lists(), "tradeDirections"] as const,
  reportStatuses: () => [...classifierKeys.lists(), "reportStatuses"] as const,
  reportSummaryStatuses: () =>
    [...classifierKeys.lists(), "reportSummaryStatuses"] as const,
  combinedNomenclature: (code: string, validationDate?: string) =>
    [
      ...classifierKeys.all(),
      "combinedNomenclature",
      { code, validationDate },
    ] as const,
  combinedNomenclatures: (validationDate?: string) =>
    [
      ...classifierKeys.lists(),
      "combinedNomenclatures",
      { validationDate },
    ] as const,
  combinedNomenclatureUnitOfMeasure: (code: string) =>
    [
      ...classifierKeys.all(),
      "combinedNomenclature",
      code,
      "unitOfMeasure",
    ] as const,
};

export async function validateCombinedNomenclatureCode(
  code: string,
  validationDate?: string
): Promise<boolean> {
  if (!code) {
    return true; // Don't validate empty strings
  }

  const queryKey = classifierKeys.combinedNomenclature(code, validationDate);

  const cached = queryClient.getQueryData<UiCombinedNomenclature | null>(
    queryKey
  );
  if (cached !== undefined) {
    return cached !== null;
  }

  try {
    const response = await sdk.getCombinedNomenclatureByCode({
      path: { code },
      query: { validationDate },
      throwOnError: true,
    });
    queryClient.setQueryData(queryKey, response.data);
    return true;
  } catch {
    queryClient.setQueryData(queryKey, null);
    return false;
  }
}

export function useCombinedNomenclatures(validationDate?: string) {
  return useQuery({
    queryKey: classifierKeys.combinedNomenclatures(validationDate),
    queryFn: async (): Promise<UiCombinedNomenclature[]> => {
      const response = await sdk.getAllCombinedNomenclatures({
        query: { validationDate },
        throwOnError: true,
      });
      return response.data;
    },
    select: data => data.sort((a, b) => a.code.localeCompare(b.code)),
    staleTime: 60 * 60 * 1000, // 1 hour
  });
}

export function useCombinedNomenclature(
  code?: string,
  validationDate?: string
) {
  return useQuery({
    queryKey: classifierKeys.combinedNomenclature(code || "", validationDate),
    queryFn: async (): Promise<UiCombinedNomenclature | null> => {
      if (!code) return null;
      try {
        const response = await sdk.getCombinedNomenclatureByCode({
          path: { code },
          query: { validationDate },
          throwOnError: true,
        });
        return response.data;
      } catch {
        return null;
      }
    },
    enabled: !!code,
  });
}

export function useCombinedNomenclatureUnitOfMeasure(code?: string) {
  return useQuery({
    queryKey: classifierKeys.combinedNomenclatureUnitOfMeasure(code || ""),
    queryFn: async (): Promise<string | null> => {
      if (!code) return null;
      try {
        const response = await sdk.getCombinedNomenclatureUnitOfMeasure({
          path: { code },
          throwOnError: true,
        });
        return response.data.unitOfMeasureCode ?? null;
      } catch {
        return null;
      }
    },
    enabled: !!code,
    staleTime: 60 * 60 * 1000,
  });
}

const sortByCode = <T extends { code: string }>(data: T[]) =>
  [...data].sort((a, b) => a.code.localeCompare(b.code));

export function useRegions(validationDate?: string) {
  return useQuery({
    queryKey: classifierKeys.regions(validationDate),
    queryFn: async (): Promise<UiRegion[]> => {
      const response = await sdk.getRegions({
        query: { validationDate },
        throwOnError: true,
      });
      return response.data;
    },
    select: sortByCode,
    staleTime: 60 * 60 * 1000, // 1 hour
  });
}

export function useNatureOfTransactions(validationDate?: string) {
  return useQuery({
    queryKey: classifierKeys.natureOfTransactions(validationDate),
    queryFn: async (): Promise<UiNatureOfTransaction[]> => {
      const response = await sdk.getNatureOfTransactions({
        query: { validationDate },
        throwOnError: true,
      });
      return response.data;
    },
    select: sortByCode,
    staleTime: 60 * 60 * 1000, // 1 hour
  });
}

export function useDeliveryTerms(validationDate?: string) {
  return useQuery({
    queryKey: classifierKeys.deliveryTerms(validationDate),
    queryFn: async (): Promise<UiDeliveryTerms[]> => {
      const response = await sdk.getDeliveryTerms({
        query: { validationDate },
        throwOnError: true,
      });
      return response.data;
    },
    select: sortByCode,
    staleTime: 60 * 60 * 1000, // 1 hour
  });
}

export function useModeOfTransports(validationDate?: string) {
  return useQuery({
    queryKey: classifierKeys.modeOfTransports(validationDate),
    queryFn: async (): Promise<UiModeOfTransport[]> => {
      const response = await sdk.getModeOfTransports({
        query: { validationDate },
        throwOnError: true,
      });
      return response.data;
    },
    select: sortByCode,
    staleTime: 60 * 60 * 1000, // 1 hour
  });
}

export function useUnitOfMeasures(validationDate?: string) {
  return useQuery({
    queryKey: classifierKeys.unitOfMeasures(validationDate),
    queryFn: async (): Promise<UiUnitOfMeasure[]> => {
      const response = await sdk.getUnitOfMeasures({
        query: { validationDate },
        throwOnError: true,
      });
      return response.data;
    },
    select: sortByCode,
    staleTime: 60 * 60 * 1000, // 1 hour
  });
}

export function useCountries(validationDate?: string) {
  return useQuery({
    queryKey: classifierKeys.countries(validationDate),
    queryFn: async (): Promise<UiCountry[]> => {
      const response = await sdk.getCountries({
        query: { validationDate },
        throwOnError: true,
      });
      return response.data;
    },
    select: sortByCode,
    staleTime: 60 * 60 * 1000, // 1 hour
  });
}

export function useTradeDirections() {
  return useQuery({
    queryKey: classifierKeys.tradeDirections(),
    queryFn: async (): Promise<UiEnumClassifier[]> => {
      const response = await sdk.getTradeDirections({
        throwOnError: true,
      });
      return response.data;
    },
    staleTime: 60 * 60 * 1000, // 1 hour
  });
}

export function useReportStatuses() {
  return useQuery({
    queryKey: classifierKeys.reportStatuses(),
    queryFn: async (): Promise<UiEnumClassifier[]> => {
      const response = await sdk.getReportStatuses({
        throwOnError: true,
      });
      return response.data;
    },
    staleTime: 60 * 60 * 1000, // 1 hour
  });
}

export function useReportSummaryStatuses() {
  return useQuery({
    queryKey: classifierKeys.reportSummaryStatuses(),
    queryFn: async (): Promise<UiEnumClassifier[]> => {
      const response = await sdk.getReportSummaryStatuses({
        throwOnError: true,
      });
      return response.data;
    },
    staleTime: 60 * 60 * 1000, // 1 hour
  });
}
